/* Automatic profiling after a harvest, on the connection's own page.
 *
 * Sits in `lq-detail-grid` beside the harvest schedule, which is where it belongs: both are
 * standing instructions about this source, and a reader deciding whether to profile automatically
 * is the reader who has just decided how often to harvest.
 *
 * **The cost note is not decoration and is not optional.** Turning this on spends money on somebody
 * else's platform — a profile is additional work against their warehouse, and on a metered one that
 * is a line on their bill. A control whose consequence a reader learns from an invoice is a control
 * that gets switched on once and blamed later, so the sentence sits beside the switch and says both
 * things: the work is spread out, and it costs.
 *
 * Deliberately in plain language. The reader here is a data owner deciding about their own
 * warehouse spend, not an engineer: nothing on this card says concurrency, worker, queue, admission
 * or aggregate scan, and the two figures it shows are counts of tables rather than of jobs.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { AutoProfile } from '@/shared/api/generated/catalog';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';

import {
  useAutoProfile,
  useRetryFailedAutoProfiles,
  useSetAutoProfileEnabled,
} from '../api/autoProfile';
import { canRetry, needsAttention, presenceOf, toneOf } from '../model/autoProfile';

export interface AutoProfileCardProps {
  readonly connectionId: string;
}

export function AutoProfileCard({ connectionId }: AutoProfileCardProps): ReactElement {
  const { t } = useTranslation();
  const profile = useAutoProfile(connectionId);
  const setEnabled = useSetAutoProfileEnabled();
  const retry = useRetryFailedAutoProfiles();
  const [confirming, setConfirming] = useState(false);

  /* **A failed read is reported, never rendered as "off".** Those are a claim about this source and
     a failure of the request, and §9 records the same conflation shipping three times — on the
     anomaly screen, the evals screen and the profiler, each time as a sentence about a customer's
     data used to report a broken fetch. A 403 is its own branch, because being unable to read the
     setting and being unable to change it send a reader to different people. */
  if (profile.isError) {
    const forbidden = profile.error instanceof ApiError && profile.error.status === 403;
    return (
      <Card title={t('autoProfile.title')}>
        <p className="lq-sched__detail">
          {forbidden ? t('autoProfile.forbidden') : t('autoProfile.readFailed')}
        </p>
      </Card>
    );
  }

  if (!profile.isSuccess) {
    return (
      <Card title={t('autoProfile.title')}>
        <p className="lq-sched__detail">{t('scheduling.loading')}</p>
      </Card>
    );
  }

  const stored: AutoProfile = profile.data;
  const presence = presenceOf(stored);
  const counts = stored.queue;
  const tone = toneOf(presence, counts);

  /* The refusal the server sent, verbatim. **Not a rewritten sentence**: a 403 here means the caller
     may not profile this tenant's datasets, which is more specific than anything this component
     could compose from a status code. */
  const refusedWrite =
    (setEnabled.error instanceof ApiError && setEnabled.error.status === 403) ||
    (retry.error instanceof ApiError && retry.error.status === 403);

  const applyEnabled = (next: boolean): void => {
    setEnabled.mutate({ connectionId, enabled: next });
  };

  return (
    <Card
      title={t('autoProfile.title')}
      hint={
        <span className={`lq-sched__chip lq-sched--${tone}`}>
          {t(chipKey(presence.kind))}
        </span>
      }
    >
      <p className="lq-sched__describes">{t(describesKey(presence.kind))}</p>

      {/* **The cost and batching note, always visible — on and off alike.**
          Shown while it is off because that is when somebody is deciding, and shown while it is on
          because the person reading a surprising bill is looking at an enabled card. Hiding it
          behind the enabled state would put the explanation exactly where nobody needs it. */}
      <div className="lq-band lq-band--info lq-band--closing">
        <p>{t('autoProfile.costNote')}</p>
      </div>

      {presence.kind === 'working' ? (
        <p className="lq-sched__detail">
          {t('autoProfile.outstanding', { count: presence.outstanding })}
        </p>
      ) : null}

      {stored.enabled && counts.succeeded > 0 ? (
        <p className="lq-sched__detail">
          {t('autoProfile.completed', { count: counts.succeeded })}
        </p>
      ) : null}

      {/* **`failed` is its own line and its own control**, because it is the only figure here that
          does not resolve by itself: the platform stops trying once attempts are spent, so those
          tables stay unprofiled until somebody acts. Folding it into the count above would bury
          the one number that needs a person. */}
      {needsAttention(counts) ? (
        <p className="lq-sched__inline lq-sched--warn">
          {t('autoProfile.needsAttention', { count: counts.failed })}
        </p>
      ) : null}

      {stored.configured && stored.configured_by !== null ? (
        <p className="lq-sched__note">
          {t('autoProfile.configuredBy', { who: stored.configured_by })}
        </p>
      ) : null}

      {setEnabled.isError && !refusedWrite ? (
        <p className="lq-sched__inline lq-sched--danger">
          {setEnabled.error instanceof ApiError
            ? setEnabled.error.message
            : t('autoProfile.toggleFailed')}
        </p>
      ) : null}

      {/* **The read-only card, reached by a refusal rather than by a permission list.** Reading this
          setting is a connection read and changing it is `catalog.dataset:profile`, so a reader
          holding the first and not the second sees the facts and cannot change them. There is no
          grant list on the wire to check in advance, so the sentence appears once a write comes back
          refused — and the controls go with it, because a button known to be refused is one that
          invites a second press. */}
      {refusedWrite ? (
        <p className="lq-sched__note">{t('autoProfile.forbidden')}</p>
      ) : (
        <div className="lq-sched__actions">
          <Button
            tone={stored.enabled ? 'secondary' : 'primary'}
            busy={setEnabled.isPending}
            onClick={() => {
              /* **Enabling asks; disabling does not.** Turning it on starts spending money on
                 somebody's warehouse, which is the direction worth a second look. Turning it off
                 stops that, and a confirmation in front of *stopping* a cost is a dialog somebody
                 clicks through while the bill runs. */
              if (stored.enabled) {
                applyEnabled(false);
              } else {
                setConfirming(true);
              }
            }}
          >
            {stored.enabled ? t('autoProfile.turnOff') : t('autoProfile.turnOn')}
          </Button>
          {canRetry(counts) ? (
            <Button
              tone="secondary"
              busy={retry.isPending}
              onClick={() => {
                retry.mutate(connectionId);
              }}
            >
              {t('autoProfile.retry')}
            </Button>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('autoProfile.confirm.title')}
        message={t('autoProfile.confirm.body')}
        confirmLabel={t('autoProfile.turnOn')}
        cancelLabel={t('autoProfile.confirm.cancel')}
        busy={setEnabled.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          setConfirming(false);
          applyEnabled(true);
        }}
      />
    </Card>
  );
}

/* Two total maps rather than two `switch`es with a `default:`. A fifth presence is then a compile
   error here as well as in the model, which is where §9 records `verdictTone`'s `default:` silently
   rendering an unknown verdict in the permitted tone. */
const CHIP: Record<string, string> = {
  unconfigured: 'autoProfile.state.unconfigured',
  off: 'autoProfile.state.off',
  idle: 'autoProfile.state.idle',
  working: 'autoProfile.state.working',
};

const DESCRIBES: Record<string, string> = {
  unconfigured: 'autoProfile.describes.unconfigured',
  off: 'autoProfile.describes.off',
  idle: 'autoProfile.describes.idle',
  working: 'autoProfile.describes.working',
};

function chipKey(kind: string): string {
  return CHIP[kind] ?? 'autoProfile.state.off';
}

function describesKey(kind: string): string {
  return DESCRIBES[kind] ?? 'autoProfile.describes.off';
}
